import { createContext, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { v4 as uuidv4 } from 'uuid';

const A2AApiContext = createContext(null);

// Helper to safely extract raw base64 from various formats
const extractBase64ForSending = (file) => {
  // Prefer rawBase64 if explicitly stored (from agent artifacts)
  if (file.rawBase64) {
    return file.rawBase64;
  }

  const source = file.base64Preview || file.base64File || file.url;
  if (!source || typeof source !== 'string') {
    return null;
  }

  // If it's a data URL, extract the base64 portion after the comma
  if (source.startsWith('data:')) {
    const commaIdx = source.indexOf(',');
    if (commaIdx !== -1) {
      return source.slice(commaIdx + 1);
    }
  }

  // If it's an http/https URL, we can't extract base64
  if (source.startsWith('http://') || source.startsWith('https://')) {
    return null;
  }

  // Assume it's already raw base64
  return source;
};

const buildParts = (text, files) => {
  const parts = [];

  if (text?.trim()) {
    parts.push({
      kind: 'text',
      text: text,
    });
  }

  if (files?.length > 0) {
    parts.push(
      ...files
        .map((file) => {
          const base64Data = extractBase64ForSending(file);

          // Skip files without valid base64 data
          if (!base64Data) {
            console.warn('Skipping file without valid base64 data:', file.name);
            return null;
          }

          return {
            kind: 'file',
            file: {
              name: file.name,
              mime_type: file.type,
              bytes: base64Data,
            },
          };
        })
        .filter(Boolean),
    );
  }

  return parts;
};

export function A2AApiProvider({ children }) {
  const { get, postStream } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getCard = async (agentId) => {
    try {
      const response = await get(`/v1/agents/int/${agentId}/.well-known/agent-card.json`);
      return response.data;
    } catch (error) {
      console.error('Error fetching A2A card:', error);
      throw error;
    }
  };

  const sendMessage = async (
    agentId,
    input,
    files,
    conversation,
    callback,
    image_quality = 85,
    min_short_side = 800,
    min_file_size = 80 * 1024,
  ) => {
    const sendParams = {
      jsonrpc: '2.0',
      method: 'message/stream',
      id: uuidv4(),
      params: {
        message: {
          messageId: uuidv4(),
          role: 'user',
          kind: 'message',
          contextId: uuidv4(),
          parts: buildParts(input, files),
          extensions: [
            'https://obriy.ai/sure/extensions/conversation/v1',
            'https://obriy.ai/sure/extensions/compression/images/v1',
          ],
          metadata: {
            'https://obriy.ai/sure/extensions/conversation/v1': {
              messages: conversation.slice(0, -1).map((msg) => ({
                role: msg.from === 'assistant' ? 'agent' : 'user',
                parts: buildParts(msg.rawText, [
                  ...(msg.audio ? [msg.audio] : []),
                  ...(msg.files ?? []),
                  ...(msg.images ?? []),
                ]),
              })),
            },
            'https://obriy.ai/sure/extensions/compression/images/v1': {
              image_quality: image_quality,
              min_short_side: min_short_side,
              min_file_size: min_file_size,
            },
          },
        },
      },
    };

    try {
      const response = await postStream(`/v1/agents/int/${agentId}/`, sendParams);

      const stream = response.data;

      const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

      let buffer = '';
      // Attempts to parse an event (after "data:"). If JSON is incomplete — returns null.
      const tryParseEvent = (rawEvent) => {
        const dataLines = rawEvent
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.replace(/^data:\s?/, ''));

        if (dataLines.length === 0) return { ok: true, data: null };

        const data = dataLines.join('\n');
        if (data === '[DONE]') return { ok: true, data: null };

        try {
          const parsed = JSON.parse(data);
          return { ok: true, data: parsed, raw: data };
        } catch {
          return { ok: false };
        }
      };

      const flushEvents = () => {
        while (buffer.length > 0) {
          // try to find an explicit SSE delimiter \n\n
          const sepIdx = buffer.indexOf('\n\n');

          if (sepIdx !== -1) {
            const rawEvent = buffer.slice(0, sepIdx);
            const rest = buffer.slice(sepIdx + 2);

            const result = tryParseEvent(rawEvent);
            if (result.ok) {
              buffer = rest;
              if (result.data !== null) {
                console.log('event response', result.raw);
                callback(result.raw);
              }
              continue;
            }
          }

          // If JSON is complete — emit event, otherwise wait for next chunk.
          if (buffer.includes('data:')) {
            const result = tryParseEvent(buffer);
            if (result.ok && result.data !== null) {
              console.log('event response', result.raw);
              callback(result.raw);
              buffer = '';
              continue;
            }
          }

          // JSON is still incomplete — exit and wait for next chunk
          break;
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        console.log('read buffer', value);

        if (value) {
          buffer += value.replace(/\r\n/g, '\n');
          flushEvents();
        }

        if (done) {
          break;
        }
      }

      // Final flush — in case the last event arrived without \n\n
      if (buffer.trim()) {
        flushEvents();
        if (buffer.trim()) {
          console.warn('Stream ended with unparsed data in buffer:', buffer);
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error sending message to agent:', error);
      throw error;
    }
  };

  return (
    <A2AApiContext.Provider value={{ getCard, sendMessage }}>{children}</A2AApiContext.Provider>
  );
}

export function useA2AApi() {
  const context = useContext(A2AApiContext);
  if (!context) {
    throw new Error('useA2AApi must be used within an A2AApiProvider');
  }
  return context;
}
